<template>
    <div class="vue-need-bootstrap h100">
        <c-loader :value="isLoading" :text="loaderText" />

        <div class="sign-up-page d-flex justify-content-center align-items-center w-100 h-100 gradient-customily-bg">
            <div class="card sign-up-card">
                <div class="card-body">
                    <div class="text-center mb-4">
                        <img class="sign-up-logo" :src="CustomilyImg" alt="Customily" />
                        <h5 class="mt-2 mb-0">
                            <b>Register your new account to join our Affiliate's Program!</b>
                        </h5>
                    </div>

                    <!-- Registration confirmation -->
                    <div v-if="registered" class="text-center">
                        <h4 class="mb-3"><b>Thank you!</b></h4>
                        <p class="mb-0">
                            Thanks for registering to Customily's Affiliate Program. Log in to your new account
                            <a href="/signin">here</a> to find your affiliate link and see your dashboard.
                        </p>
                    </div>

                    <validation-observer v-else v-slot="{ handleSubmit, valid }">
                        <form @submit.stop.prevent="handleSubmit(signUp)">
                            <validation-provider v-slot="{ errors, failed }" rules="required" name="Name">
                                <c-input
                                    v-model="form.name"
                                    class="mb-3"
                                    type="text"
                                    label="Name"
                                    placeholder="Type your name"
                                    :error="failed"
                                    :error-msg="errors[0]"
                                />
                            </validation-provider>

                            <div class="form-row">
                                <div
                                    v-tippy="{
                                        content:
                                            'This field cannot be changed afterwards, it will be the ID in the affiliate link that you will share'
                                    }"
                                    class="col-12 col-md-6"
                                >
                                    <validation-provider
                                        v-slot="{ errors, failed }"
                                        rules="required|alpha_num"
                                        name="Username"
                                    >
                                        <c-input
                                            v-model="form.username"
                                            class="mb-3"
                                            type="text"
                                            label="Username"
                                            placeholder="Type your username"
                                            :error="failed"
                                            :error-msg="errors[0]"
                                        />
                                    </validation-provider>
                                </div>

                                <div class="col-12 col-md-6">
                                    <validation-provider
                                        v-slot="{ errors, failed }"
                                        rules="required|email"
                                        name="Email"
                                    >
                                        <c-input
                                            v-model="form.email"
                                            class="mb-3"
                                            type="email"
                                            label="Email"
                                            placeholder="Type your email"
                                            :error="failed"
                                            :error-msg="errors[0]"
                                        />
                                    </validation-provider>
                                </div>

                                <div class="col-12 col-md-6">
                                    <validation-provider
                                        v-slot="{ errors, failed }"
                                        rules="required|min:8"
                                        name="Password"
                                        vid="password"
                                    >
                                        <c-input
                                            v-model="form.password"
                                            class="mb-3"
                                            type="password"
                                            label="Password"
                                            placeholder="Type your password"
                                            :error="failed"
                                            :error-msg="errors[0]"
                                        />
                                    </validation-provider>
                                </div>

                                <div class="col-12 col-md-6">
                                    <validation-provider
                                        v-slot="{ errors, failed }"
                                        rules="required|min:8|password-confirmed:password"
                                        name="Password confirmation"
                                    >
                                        <c-input
                                            v-model="form.passwordConfirmation"
                                            class="mb-3"
                                            type="password"
                                            label="Confirm Password"
                                            placeholder="Re-enter your password"
                                            :error="failed"
                                            :error-msg="errors[0]"
                                        />
                                    </validation-provider>
                                </div>
                            </div>

                            <validation-provider v-slot="{ errors, failed }" rules="required" name="Captcha">
                                <c-recaptcha
                                    ref="recaptcha"
                                    v-model="form.captchaToken"
                                    class="mb-3"
                                    :error="failed"
                                    :error-msg="errors[0]"
                                />
                            </validation-provider>

                            <div v-if="errorMessage" class="error-message w-100 pb-3 text-center">
                                {{ errorMessage }}
                            </div>

                            <c-button
                                class="w-100"
                                variant="primary"
                                type="submit"
                                text="Sign Up"
                                :disabled="!valid || isLoading"
                            />
                        </form>
                    </validation-observer>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, reactive, ref } from 'vue';
import { AxiosError } from 'axios';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import CLoader from '@/components/shared/CLoader.vue';
import CRecaptcha, { CRecaptchaRef } from '@/components/shared/CRecaptcha';
import { registerAffiliate } from '@/api/user';
import { useLoader } from '@/composables/loader';
import { toRegistrationErrorMessage } from './registrationError';
import CustomilyImg from '@/assets/images/customily-logo.png';

const form = reactive({
    name: '',
    username: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    captchaToken: ''
});

const registered = ref(false);
const errorMessage = ref('');
const recaptcha: Ref<CRecaptchaRef | undefined> = ref();

const { display, isLoading, loaderText } = useLoader();

const signUp = async () => {
    errorMessage.value = '';
    display(true, 'Creating your account...');

    try {
        await registerAffiliate({
            Name: form.name,
            Username: form.username,
            Email: form.email,
            Password: form.password,
            ConfirmationPassword: form.passwordConfirmation,
            Captcha: form.captchaToken
        });

        registered.value = true;
    } catch (error) {
        recaptcha.value?.reset();
        errorMessage.value = toRegistrationErrorMessage(error as AxiosError);
    } finally {
        display(false);
    }
};
</script>

<style lang="scss" scoped>
.sign-up-page {
    overflow-y: auto;

    .sign-up-card {
        width: 100%;
        max-width: 40rem;
        margin: 1.5rem;
    }

    .sign-up-logo {
        width: 80px;
    }
}
</style>
